import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Users, CheckCircle, CalendarOff, AlertCircle, Megaphone, Calendar,
  Cake, Award, Quote, ChevronLeft, ChevronRight, Sparkles, UserCheck, Clock, AlertTriangle
} from 'lucide-react';
import { getUser, isAdmin, isManager } from '@/lib/auth';
import api from '@/lib/api';
import ClockInOutCard from '@/components/ClockInOutCard';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function getDaysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}
function getFirstDayOfMonth(year, month) {
  return new Date(year, month - 1, 1).getDay();
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState(null);
  const [quote, setQuote] = useState(null);
  const [birthdays, setBirthdays] = useState([]);
  const [anniversaries, setAnniversaries] = useState([]);
  const [calendarData, setCalendarData] = useState(null);
  const [mgrData, setMgrData] = useState(null);
  const [loading, setLoading] = useState(true);

  const now = new Date();
  const [calMonth, setCalMonth] = useState(now.getMonth() + 1);
  const [calYear, setCalYear] = useState(now.getFullYear());

  const user = getUser();
  const admin = isAdmin();
  const manager = isManager() || admin;

  useEffect(() => {
    const load = async () => {
      try {
        const [s, a, q, b, an] = await Promise.all([
          api.get('/api/dashboard/stats'),
          api.get('/api/dashboard/recent-activities'),
          api.get('/api/quotes/today'),
          api.get('/api/dashboard/birthdays'),
          api.get('/api/dashboard/anniversaries'),
        ]);
        setStats(s.data);
        setActivities(a.data);
        setQuote(q.data);
        setBirthdays(b.data);
        setAnniversaries(an.data);
        if (isManager() || isAdmin()) {
          try { const m = await api.get('/api/dashboard/manager'); setMgrData(m.data); } catch {}
        }
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  const loadCalendar = useCallback(async () => {
    try {
      const { data } = await api.get('/api/dashboard/calendar-data', { params: { month: calMonth, year: calYear } });
      setCalendarData(data);
    } catch (e) { console.error(e); }
  }, [calMonth, calYear]);

  useEffect(() => { loadCalendar(); }, [loadCalendar]);

  const prevMonth = () => {
    if (calMonth === 1) { setCalMonth(12); setCalYear(calYear - 1); }
    else setCalMonth(calMonth - 1);
  };
  const nextMonth = () => {
    if (calMonth === 12) { setCalMonth(1); setCalYear(calYear + 1); }
    else setCalMonth(calMonth + 1);
  };

  const today = new Date();
  const dateStr = today.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  if (loading) return (
    <div className="space-y-6">
      <Skeleton className="h-20 rounded-xl" />
      <Skeleton className="h-16 rounded-xl" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4"><Skeleton className="h-28 rounded-xl" /><Skeleton className="h-28 rounded-xl" /><Skeleton className="h-28 rounded-xl" /><Skeleton className="h-28 rounded-xl" /></div>
    </div>
  );

  // Build calendar day data
  const daysInMonth = getDaysInMonth(calYear, calMonth);
  const firstDay = getFirstDayOfMonth(calYear, calMonth);
  const holidayMap = {};
  const leaveMap = {};
  if (calendarData) {
    for (const h of calendarData.holidays) {
      const day = parseInt(h.date.split('-')[2]);
      if (!holidayMap[day]) holidayMap[day] = [];
      holidayMap[day].push(h);
    }
    for (const l of calendarData.leaves) {
      const start = new Date(l.start_date);
      const end = new Date(l.end_date);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        if (d.getMonth() + 1 === calMonth && d.getFullYear() === calYear) {
          const day = d.getDate();
          if (!leaveMap[day]) leaveMap[day] = [];
          leaveMap[day].push(l);
        }
      }
    }
  }

  const kpis = admin ? [
    { label: 'Total Employees', value: stats?.total_employees || 0, icon: Users, color: 'text-[#0D2850]', bg: 'bg-blue-50' },
    { label: 'Present Today', value: stats?.present_today || 0, icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50', sub: `${stats?.attendance_percentage || 0}%` },
    { label: 'On Leave', value: stats?.on_leave_today || 0, icon: CalendarOff, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'Pending', value: stats?.pending_approvals || 0, icon: AlertCircle, color: 'text-rose-600', bg: 'bg-rose-50' },
  ] : [
    { label: 'My Pending Leaves', value: stats?.pending_approvals || 0, icon: AlertCircle, color: 'text-rose-600', bg: 'bg-rose-50' },
  ];

  const isToday = (day) => day === now.getDate() && calMonth === now.getMonth() + 1 && calYear === now.getFullYear();
  const isSunday = (day) => new Date(calYear, calMonth - 1, day).getDay() === 0;

  return (
    <div className="space-y-5" data-testid="dashboard-page">
      {/* Welcome + Date */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2">
        <div>
          <Badge className="bg-[#0D2850] text-white text-[10px] mb-1.5 hover:bg-[#0D2850]">// DASHBOARD</Badge>
          <h1 className="text-2xl font-semibold text-[#0B1B33] tracking-tight">Welcome back, {user?.name || 'Admin'}</h1>
          <p className="text-sm text-slate-500">{dateStr}</p>
        </div>
      </div>

      {/* Quote of the Day */}
      {quote && (
        <Card className="border-slate-200 bg-gradient-to-r from-[#0D2850] to-[#1a3a6a] text-white" data-testid="quote-card">
          <CardContent className="py-4 px-5 flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-[#F5CB08] shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium leading-relaxed italic">"{quote.quote}"</p>
              <p className="text-xs text-slate-300 mt-1">— {quote.author}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Birthdays + Anniversaries Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-slate-200" data-testid="birthdays-card">
          <CardHeader className="py-3 px-4 pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Cake className="w-4 h-4 text-pink-500" /> Birthdays This Month
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            {birthdays.length === 0 ? (
              <p className="text-xs text-slate-400 py-2">No birthdays this month. Admin can add DOB in employee profiles.</p>
            ) : (
              <div className="space-y-2">
                {birthdays.map((b, i) => (
                  <div key={i} className={`flex items-center gap-2.5 py-1.5 px-2 rounded-md ${b.is_today ? 'bg-pink-50 border border-pink-200' : ''}`}>
                    <Avatar className="w-7 h-7">
                      <AvatarFallback className={`text-[10px] ${b.is_today ? 'bg-pink-500 text-white' : 'bg-[#0D2850] text-white'}`}>
                        {b.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{b.name}</p>
                      <p className="text-[10px] text-slate-400">{b.department}</p>
                    </div>
                    {b.is_today ? (
                      <Badge className="bg-pink-100 text-pink-700 text-[10px] hover:bg-pink-100">Today!</Badge>
                    ) : (
                      <span className="text-[10px] text-slate-400">{b.date_of_birth?.split('-')[2]}/{MONTH_NAMES[calMonth - 1].slice(0, 3)}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="anniversaries-card">
          <CardHeader className="py-3 px-4 pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Award className="w-4 h-4 text-amber-500" /> Work Anniversaries This Month
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            {anniversaries.length === 0 ? (
              <p className="text-xs text-slate-400 py-2">No work anniversaries this month</p>
            ) : (
              <div className="space-y-2">
                {anniversaries.map((a, i) => (
                  <div key={i} className={`flex items-center gap-2.5 py-1.5 px-2 rounded-md ${a.is_today ? 'bg-amber-50 border border-amber-200' : ''}`}>
                    <Avatar className="w-7 h-7">
                      <AvatarFallback className={`text-[10px] ${a.is_today ? 'bg-amber-500 text-white' : 'bg-[#0D2850] text-white'}`}>
                        {a.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{a.name}</p>
                      <p className="text-[10px] text-slate-400">{a.department}</p>
                    </div>
                    <Badge className={`text-[10px] ${a.is_today ? 'bg-amber-100 text-amber-700 hover:bg-amber-100' : 'bg-slate-100 text-slate-600 hover:bg-slate-100'}`}>
                      {a.years} yr{a.years > 1 ? 's' : ''}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Clock In/Out Card for employees */}
      {!admin && <ClockInOutCard />}

      {/* Manager Dashboard Section */}
      {manager && mgrData && mgrData.reports && mgrData.reports.length > 0 && (
        <Card className="border-slate-200" data-testid="manager-dashboard">
          <CardHeader className="py-3 px-4 pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <UserCheck className="w-4 h-4 text-[#0D2850]" /> My Team Today
              <span className="text-xs font-normal text-slate-400 ml-1">{mgrData.summary?.total_reports || 0} direct reports</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-4">
              <div className="text-center p-2 bg-slate-50 rounded-lg">
                <div className="text-lg font-semibold text-[#0D2850]">{mgrData.summary?.total_reports || 0}</div>
                <div className="text-[10px] text-slate-500">Total</div>
              </div>
              <div className="text-center p-2 bg-emerald-50 rounded-lg">
                <div className="text-lg font-semibold text-emerald-600">{mgrData.summary?.present || 0}</div>
                <div className="text-[10px] text-slate-500">Present</div>
              </div>
              <div className="text-center p-2 bg-amber-50 rounded-lg">
                <div className="text-lg font-semibold text-amber-600">{mgrData.summary?.on_leave || 0}</div>
                <div className="text-[10px] text-slate-500">On Leave</div>
              </div>
              <div className="text-center p-2 bg-red-50 rounded-lg">
                <div className="text-lg font-semibold text-red-600">{mgrData.summary?.absent || 0}</div>
                <div className="text-[10px] text-slate-500">Absent</div>
              </div>
              <div className="text-center p-2 bg-blue-50 rounded-lg">
                <div className="text-lg font-semibold text-blue-600">{mgrData.summary?.kra_evaluated || 0}</div>
                <div className="text-[10px] text-slate-500">KRA Done</div>
              </div>
              <div className="text-center p-2 bg-purple-50 rounded-lg">
                <div className="text-lg font-semibold text-purple-600">{mgrData.summary?.pending_approvals || 0}</div>
                <div className="text-[10px] text-slate-500">Pending Leaves</div>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs">Employee</TableHead>
                  <TableHead className="text-xs">Department</TableHead>
                  <TableHead className="text-xs text-center">Status</TableHead>
                  <TableHead className="text-xs">Clock In</TableHead>
                  <TableHead className="text-xs text-center">KRA %</TableHead>
                  <TableHead className="text-xs text-center">Docs</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mgrData.reports.map(r => (
                  <TableRow key={r.id} data-testid={`team-member-${r.id}`}>
                    <TableCell className="text-sm font-medium">{r.name}</TableCell>
                    <TableCell className="text-xs text-slate-500">{r.department}</TableCell>
                    <TableCell className="text-center">
                      <Badge className={`text-[10px] ${r.today_status === 'present' ? 'bg-emerald-100 text-emerald-700' : r.today_status === 'on_leave' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                        {r.today_status === 'present' ? 'Present' : r.today_status === 'on_leave' ? r.leave_type || 'Leave' : 'Absent'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{r.clock_in ? new Date(r.clock_in).toLocaleTimeString('en-IN', {hour: '2-digit', minute: '2-digit'}) : '-'}</TableCell>
                    <TableCell className="text-center">
                      {r.kra_score > 0 ? (
                        <Badge className={`text-[10px] ${r.kra_score >= 81 ? 'bg-emerald-100 text-emerald-700' : r.kra_score >= 61 ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>{r.kra_score}%</Badge>
                      ) : (
                        <span className="text-[10px] text-slate-300">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center text-xs">
                      {(r.docs_pending > 0 || r.docs_verified > 0) ? (
                        <span>{r.docs_verified || 0}<span className="text-slate-300">/</span>{r.docs_pending > 0 ? <span className="text-amber-600">{r.docs_pending}p</span> : null}</span>
                      ) : <span className="text-slate-300">—</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {mgrData.pending_leaves?.length > 0 && (
              <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-xs font-semibold text-amber-800 flex items-center gap-1 mb-2"><AlertTriangle className="w-3.5 h-3.5" /> {mgrData.pending_leaves.length} Pending Leave Request(s)</p>
                {mgrData.pending_leaves.slice(0, 3).map(l => (
                  <div key={l.id} className="text-xs text-amber-700 py-0.5">{l.employee_name}: {l.leave_type} ({l.start_date} to {l.end_date})</div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpis.map((kpi, i) => (
          <Card key={i} className="border-slate-200 bg-white hover:shadow-md transition-shadow" data-testid={`dashboard-kpi-${kpi.label.toLowerCase().replace(/ /g, '-')}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">{kpi.label}</p>
                  <p className="text-2xl font-semibold text-[#0B1B33] mt-0.5 font-tabular">{kpi.value}</p>
                  {kpi.sub && <p className="text-[10px] text-emerald-600 mt-0.5">{kpi.sub} attendance</p>}
                </div>
                <div className={`w-9 h-9 rounded-xl ${kpi.bg} flex items-center justify-center`}>
                  <kpi.icon className={`w-4 h-4 ${kpi.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Calendar + Sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Monthly Calendar */}
        <Card className="border-slate-200 lg:col-span-2" data-testid="calendar-card">
          <CardHeader className="py-3 px-4 pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#F5CB08]" /> {MONTH_NAMES[calMonth - 1]} {calYear}
            </CardTitle>
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={prevMonth}><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={nextMonth}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {/* Day headers */}
            <div className="grid grid-cols-7 mb-1">
              {DAY_NAMES.map(d => (
                <div key={d} className={`text-center text-[10px] font-semibold py-1 ${d === 'Sun' ? 'text-red-400' : 'text-slate-400'}`}>{d}</div>
              ))}
            </div>
            {/* Days grid */}
            <div className="grid grid-cols-7 gap-px bg-slate-100 rounded-lg overflow-hidden">
              {/* Empty cells for days before month starts */}
              {Array.from({ length: firstDay }, (_, i) => (
                <div key={`e${i}`} className="bg-white p-1 min-h-[60px]" />
              ))}
              {/* Actual days */}
              {Array.from({ length: daysInMonth }, (_, i) => {
                const day = i + 1;
                const holidays = holidayMap[day] || [];
                const leaves = leaveMap[day] || [];
                const sunday = isSunday(day);
                const todayClass = isToday(day);
                const hasFixed = holidays.some(h => h.type === 'fixed');
                const hasHalfDay = holidays.some(h => h.type === 'half_day');
                const hasFlexi = holidays.some(h => h.type === 'flexi');
                const hasApproved = leaves.some(l => l.status === 'approved');
                const hasPending = leaves.some(l => l.status === 'pending');

                return (
                  <div
                    key={day}
                    className={`bg-white p-1 min-h-[60px] relative group ${todayClass ? 'ring-2 ring-[#F5CB08] ring-inset' : ''} ${sunday ? 'bg-red-50/40' : ''}`}
                  >
                    <span className={`text-xs font-medium block text-center ${todayClass ? 'text-[#F5CB08] font-bold' : sunday ? 'text-red-400' : 'text-slate-700'}`}>
                      {day}
                    </span>
                    <div className="flex flex-wrap gap-0.5 mt-0.5 justify-center">
                      {hasFixed && <div className="w-1.5 h-1.5 rounded-full bg-red-500" title="Holiday" />}
                      {hasHalfDay && <div className="w-1.5 h-1.5 rounded-full bg-orange-400" title="Half Day" />}
                      {hasFlexi && <div className="w-1.5 h-1.5 rounded-full bg-purple-400" title="Flexi Holiday" />}
                      {hasApproved && <div className="w-1.5 h-1.5 rounded-full bg-blue-500" title="Leave Approved" />}
                      {hasPending && <div className="w-1.5 h-1.5 rounded-full bg-amber-400" title="Leave Pending" />}
                    </div>
                    {/* Tooltip on hover */}
                    {(holidays.length > 0 || leaves.length > 0) && (
                      <div className="absolute z-10 hidden group-hover:block bottom-full left-1/2 -translate-x-1/2 mb-1 bg-[#0B1B33] text-white text-[10px] rounded px-2 py-1 whitespace-nowrap shadow-lg">
                        {holidays.map((h, hi) => <div key={hi}>{h.name} ({h.type})</div>)}
                        {leaves.map((l, li) => <div key={li}>{l.employee_name}: {l.leave_type} ({l.status})</div>)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Legend */}
            <div className="flex flex-wrap gap-4 mt-3 text-[10px] text-slate-500">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Fixed Holiday</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-400" /> Half Day</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-400" /> Flexi Holiday</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" /> Leave Taken</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" /> Leave Pending</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded border-2 border-[#F5CB08]" /> Today</span>
            </div>
          </CardContent>
        </Card>

        {/* Right sidebar: Announcements + Policies */}
        <div className="space-y-4">
          <Card className="border-slate-200" data-testid="announcements-widget">
            <CardHeader className="py-3 px-4 pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Megaphone className="w-4 h-4 text-[#F5CB08]" /> Announcements
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3 space-y-2.5">
              {activities?.recent_announcements?.length > 0 ? (
                activities.recent_announcements.slice(0, 3).map((ann, i) => (
                  <div key={i} className="border-l-3 border-l-[#F5CB08] pl-2.5 py-1">
                    <p className="text-xs font-medium text-[#0B1B33] leading-tight">{ann.title}</p>
                    <p className="text-[10px] text-slate-500 mt-0.5 line-clamp-2">{ann.content}</p>
                  </div>
                ))
              ) : (
                <p className="text-xs text-slate-400">No announcements</p>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200" data-testid="policies-widget">
            <CardHeader className="py-3 px-4 pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#F5CB08]" /> Quick Info
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              <div className="space-y-2">
                {[
                  ['Office Hours', '9:30 AM - 6:30 PM'],
                  ['Weekly Off', 'Sunday'],
                  ['Total Holidays', '15-16 days/year'],
                  ['Earned Leave', '12 days/year'],
                  ['Casual Leave', '6 days/year'],
                ].map(([label, val], i) => (
                  <div key={i} className={`flex justify-between items-center py-1.5 ${i < 4 ? 'border-b border-slate-100' : ''}`}>
                    <span className="text-[11px] text-slate-500">{label}</span>
                    <span className="text-[11px] font-medium">{val}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
